import { HttpService, LoggerService, ServiceType, HttpError } from '@danangelus/ngx-app-core'
import { inject, Injectable } from '@angular/core'
import { GatewayError, Page } from "../../models/gateway.model";
import { User } from '../../models/user.model'
import { Group } from '../../models/group.model'

const log: LoggerService = LoggerService.get('LdapManagerService')

/**
 * Service to communicate with the LDAP Manager, retrieves and manages
 * Group and User maintenance
 *
 * @author Connor Wheatley
 * @author Hayden Eastwell
 */
@Injectable({
    providedIn: 'root',
})
export class LdapManagerService {

    private http: HttpService = inject(HttpService)

    /**
     * Retrieve a collection of LDAP Users with an optional query string
     * to search against:
     * <ul>
     *     <li>ID</li>
     *     <li>Name</li>
     *     <li>Dealer Name</li>
     * </ul>
     *
     * If no query is provided, all applicable LDAP Users are extracted
     *
     * @param query Optional string value for a query
     * @param findSubUsers Indicates to find Users managed by the logged-in User
     * @param page Page to retrieve
     * @param pageSize Max results size
     * @param sort Column to sort by (inc direction)
     */
    async findUsers(
        query?: string | undefined,
        findSubUsers = false,
        page?: number,
        pageSize?: number,
        sort?: string,
    ): Promise<Page | null> {
        try {
            return this.http.get(
                ServiceType.LDAP_MANAGER,
                'get-users',
                null, {
                    findSubUsers: findSubUsers,
                    query: query || null,
                    pageNumber: page || null,
                    pageSize: pageSize || null,
                    sort: sort || null
                },
                false,
                true
            )
        } catch (err: any) {
            log.error(`There was an error when attempting to get user data: ${err}`)
        }
        return null
    }

    /**
     * Retrieve a specific User by their CN
     *
     * @param id unique id and username
     */
    async getUser(id: string): Promise<GatewayError | User | undefined> {
        try {
            const response = await this.http.get(ServiceType.LDAP_MANAGER, 'load-user', { id, includeGroupDn: false, includeFullDn: false }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body.error,
                    message: response.body.message
                })
            }
            return new User(response)
        } catch (err: any) {
            log.error(`There was an error when attempting to get data for User DN [${id}]: ${err}`)
        }
        return undefined
    }

    /**
     * Save or Update an LDAP User
     *
     * @param user User object payload
     * @param managed Is the User a managed/sub-User?
     */
    async createUser(user: User, managed = false): Promise<GatewayError | boolean> {
        try {
            let response
            if (managed){
                response = await this.http.post(ServiceType.LDAP_MANAGER, 'create-managed-user', user, null, { password: user.password || '' }, false, true)
            } else {
                response = await this.http.post(ServiceType.LDAP_MANAGER, 'create-user', user, null, { password: user.password || '' }, false, true)
            }

            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body.error,
                    message: response.body.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error when attempting to submit User data for saving: ${err}`)
        }
        return false
    }

    async getSubIdSuggestion(): Promise<GatewayError | string | undefined> {
        try {
            const response = await this.http.get(ServiceType.LDAP_MANAGER, 'get-sub-id', null, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body.error,
                    message: response.body.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error when attempting to get a new Sub User ID suggestion: ${err}`)
        }
        return undefined
    }

    /**
     * Save or Update an LDAP User
     *
     * @param user User object payload
     */
    async saveUser(user: User, resetMfa = false): Promise<GatewayError | boolean> {
        try {
            const response = await this.http.put(ServiceType.LDAP_MANAGER, 'save-user', user, { id: user.userId }, { password: user.password || '', resetMfa }, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error when attempting to submit User data for saving: ${err}`)
        }
        return false
    }

    /**
     * Send a request to delete a User with their `userId`
     *
     * @param userId User's userId field
     * @param adminUserId ID of the User's admin
     */
    async deleteUser(userId: string, adminUserId?: string | null): Promise<GatewayError | boolean> {
        try {
            const response = await this.http.delete(ServiceType.LDAP_MANAGER, 'delete-user', { id: userId, adminUserId: adminUserId }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body.error,
                    message: response.body.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error when attempting to delete User [${userId}]: ${err}`)
        }
        return false
    }

    /**
     * Retrieve the groups that are stored in the LDAP
     *
     * @returns A list of all groups
     */
    async findGroups(
        query?: string | undefined,
        page?: number,
        pageSize?: number,
        sort?: string,
    ): Promise<Page | null> {
        try {
            return await this.http.get(
                ServiceType.LDAP_MANAGER,
                'get-groups',
                null,
                {
                    query: query || null,
                    pageNumber: page || null,
                    pageSize: pageSize || null,
                    sort: sort || null
                },
                false,
                true
            ) as Page

        } catch (err: any) {
            log.error(`There was an error attempting to retrieve group data! ${err}`)
        }
        return null
    }

    /**
     * Retrieve all groups from the LDAP without any paging/sorting
     *
     * @returns A list of all groups from the LDAP
     */
    async getAllGroups(): Promise<Group[]> {
        try {
            return await this.http.get(ServiceType.LDAP_MANAGER, 'get-all-groups', null, null, false, true) as Group[]
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve group data! ${err}`)
        }
        return []
    }

    /**
     * Retrieve the group with the given group ID
     *
     * @param id The ID of the group
     * @returns The group with the given ID
     */
    async loadGroup(id: string): Promise<Group|undefined> {
        try {
            log.info('loadGroup', `Loading group with ID: [${id}]`)
            const response: any = await this.http.get(ServiceType.LDAP_MANAGER, 'load-group', { id }, null, false, true)
            // ToDo :: Handle error response
            return new Group(response)
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve data for this group! ${err}`)
        }
        return
    }

    /**
     * Creates a new group with the given name in the LDAP.
     *
     * @param group The new group to create
     * @returns true if the group was created, false if there was an error, and null if the group already exists
     */
    async createGroup(group: Group): Promise<GatewayError | boolean> {
        try {
            log.info('createGroup', `Creating group with ID: [${group.groupId}]`)
            const response = await this.http.post(ServiceType.LDAP_MANAGER, 'create-group', group, null, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error attempting to create a new group! ${err}`)
        }
        return false
    }

    /**
     * Edits an existing group
     *
     * @param group The group with the updated data
     * @param groupId The ID of the group with updated data
     * @returns true if the group was edited, false if there was an error
     */
    async saveGroup(groupId: string, group: Group): Promise<GatewayError | boolean> {
        try {
            log.info('saveGroup', `Saving group with ID: [${groupId}]`)
            const response = await this.http.put(ServiceType.LDAP_MANAGER, 'save-group', group, { id: groupId }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error attempting to edit the group! ${err}`)
        }
        return false
    }

    /**
     * Deletes a group
     *
     * @param id The group ID of the group to be deleted
     * @returns true if the group was deleted, false if there was an error
     */
    async deleteGroup(id: string): Promise<GatewayError | boolean> {
        try {
            log.info('deleteGroup', `Deleting group with ID: [${id}]`)
            const response = await this.http.delete(ServiceType.LDAP_MANAGER, 'delete-group', { id }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true

        } catch (err: any) {
            log.error(`There was an error attempting to delete the group! ${err}`)
        }
        return false
    }

    /**
     * Gets the default MFA option for the specified User
     *
     * @param userId The User's ID
     * @returns the default MFA option, if it exists
     */
    async getDefaultMfaOption(userId: string): Promise<GatewayError | string> {
        try {
            log.info('getDefaultMfaOption', `Getting default MFA option for User ${userId}`)
            const response = await this.http.get(ServiceType.LDAP_MANAGER, 'get-mfa-option', { userId: userId }, null, false, true, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response,
                    message: response.body!.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error when attempting to get the default MFA option! ${err}`)
            return new GatewayError(err)
        }
    }

    /**
     * Sets the default MFA option for the specified User
     *
     * @param userId The User's ID
     * @param mfaOption The chosen MFA option
     */
    async setDefaultMfaOption(userId: string, mfaOption: string): Promise<GatewayError | void> {
        try {
            log.info('setDefaultMfaOption', `Setting default MFA option for User ${userId}`)
            const response = await this.http.post(ServiceType.LDAP_MANAGER, 'save-mfa-option', { userId: userId, mfaOption: mfaOption }, null, false, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response,
                    message: response.body!.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`There was an error when attempting to set the default MFA option! ${err}`)
            return new GatewayError(err)
        }
    }

}
